import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  ChevronRight,
  Link as LinkIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { Button } from '@/components/ui/CustomButton';
import SlackLinkPage from '@gokrew/ui/components/SlackLinkPage';
import { SlackLogo } from '@gokrew/ui/components/SlackLogo';
import { AppLoaderPage } from '@gokrew/ui/components/AppLoaderPage';
import { KollabHRLogo } from '@gokrew/ui/assets';
import { linkSlackWorkspace } from '@/api/slack-link';
import { useAuth } from '@/context/AuthContext';
import type { Company } from '@/types/company';
import styles from './WorkspaceLinkPage.module.css';
import { companyDisplayName } from '@gokrew/ui/utility/companyDisplayName';

export default function WorkspaceLinkPage() {
  const { companies, isLoading, changeCompany } = useAuth();
  const { t } = useTranslation(TranslationNS.slack);
  const navigate = useNavigate();
  const [chosenCompany, setChosenCompany] = useState<Company | null>(null);
  const [highlighted, setHighlighted] = useState<Company | null>(null);

  const list = useMemo(() => companies ?? [], [companies]);
  const effectiveCompany =
    chosenCompany ?? (list.length === 1 ? list[0] : null);

  useEffect(() => {
    if (effectiveCompany) changeCompany(effectiveCompany);
  }, [effectiveCompany?.id, changeCompany]);

  if (isLoading) return <AppLoaderPage />;

  if (!list.length) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.card}>
          <div className={styles.emptyState}>
            <div className={styles.emptyIconWrap}>
              <AlertCircle size={32} className={styles.emptyIcon} />
            </div>
            <div>
              <h2 className={styles.heading}>
                {t('workspaceLink.selectCompany.no_companies_title')}
              </h2>
              <p className={styles.subtext}>
                {t('workspaceLink.selectCompany.no_companies_description')}
              </p>
            </div>
            <Button
              variant="primary"
              className={styles.fullWidthButton}
              onClick={() => navigate('/dashboard')}
            >
              {t('workspaceLink.selectCompany.back')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!effectiveCompany) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.card}>
          <div className={styles.brandRow}>
            <SlackLogo size={34} />
            <div className={styles.linkGlyph}>
              <LinkIcon size={12} />
            </div>
            <div className={styles.klogo}>
              <img
                src={KollabHRLogo}
                alt="KollabHR"
                className={styles.klogoBadge}
              />
            </div>
          </div>

          <h2 className={styles.heading}>
            {t('workspaceLink.selectCompany.heading')}
          </h2>
          <p className={styles.subtext}>
            {t('workspaceLink.selectCompany.subtext')}
          </p>

          <div className={styles.companyList}>
            {list.map((company) => {
              const isSelected = highlighted?.id === company.id;
              const cardClassName = isSelected
                ? `${styles.companyCard} ${styles.companyCardSelected}`
                : styles.companyCard;
              return (
                <button
                  key={company.id}
                  type="button"
                  className={cardClassName}
                  aria-pressed={isSelected}
                  onClick={() => setHighlighted(company)}
                >
                  <div className={styles.companyAvatar}>
                    {company.logo ? (
                      <img
                        src={company.logo}
                        alt={companyDisplayName(company)}
                        className={styles.companyAvatarImg}
                      />
                    ) : (
                      <span className={styles.companyAvatarText}>
                        {companyDisplayName(company).slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className={styles.companyInfo}>
                    <p className={styles.companyName}>
                      {companyDisplayName(company)}
                    </p>
                    <p className={styles.companyRole}>
                      {t('workspaceLink.selectCompany.role_admin')}
                    </p>
                  </div>
                  {isSelected ? (
                    <Check size={20} className={styles.checkIcon} />
                  ) : (
                    <ChevronRight size={16} className={styles.chevron} />
                  )}
                </button>
              );
            })}
          </div>

          <Button
            variant="primary"
            className={styles.fullWidthButton}
            disabled={!highlighted}
            onClick={() => highlighted && setChosenCompany(highlighted)}
          >
            {highlighted
              ? t('workspaceLink.selectCompany.connect_button', {
                  companyName: companyDisplayName(highlighted),
                })
              : t('workspaceLink.selectCompany.connect_button_empty')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SlackLinkPage
      onLink={(token) => linkSlackWorkspace(effectiveCompany.id, token)}
      dashboardPath="/dashboard"
      i18nPrefix="workspaceLink"
      companyName={companyDisplayName(effectiveCompany)}
      companyLogoUrl={effectiveCompany.logo ?? null}
    />
  );
}
